import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, BookOpen } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SubstackSection({ data, registerSection }) {
  const ref = useRef(null);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (ref.current) registerSection("substack", ref.current);
  }, [registerSection]);

  if (!data) return null;

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/subscribe`, { email });
      toast.success("Thanks \u2014 we'll send the next issue.");
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      ref={ref}
      id="substack"
      className="py-16 md:py-24"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="substack-panel rounded-2xl overflow-hidden relative">
          {/* Dark marble overlay */}
          <div
            className="absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage: `url("https://images.unsplash.com/photo-1550053808-52a75a05955d?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85")`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />

          <div className="relative z-10 grid lg:grid-cols-12 gap-8 lg:gap-12 p-8 md:p-12 lg:p-16">
            {/* Left: Content */}
            <motion.div
              className="lg:col-span-7"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5 }}
            >
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--brand-brass-2))] mb-3">
                Newsletter
              </p>
              <h2 className="font-heading text-2xl md:text-3xl tracking-[-0.01em] text-white">
                {data.section_title}
              </h2>
              <p className="mt-4 text-sm md:text-base text-white/60 leading-relaxed max-w-xl">
                {data.description}
              </p>

              {/* Latest Issue Preview */}
              {data.latest_issue && (
                <a
                  href={data.latest_issue.url || data.substack_url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/[0.08] transition-colors duration-200 group"
                  data-testid="substack-latest-issue-link"
                >
                  <BookOpen className="h-5 w-5 text-[hsl(var(--brand-brass-2))] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs text-white/40 font-mono uppercase tracking-wider">
                      Latest Issue &mdash; {data.latest_issue.date}
                    </p>
                    <p className="text-sm text-white/90 font-medium mt-1 group-hover:text-white">
                      {data.latest_issue.title}
                    </p>
                    <p className="text-xs text-white/50 mt-1">
                      {data.latest_issue.excerpt}
                    </p>
                  </div>
                </a>
              )}
            </motion.div>

            {/* Right: Signup Card */}
            <motion.div
              className="lg:col-span-5 flex items-center"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card className="rounded-2xl bg-card border-border p-6 md:p-8 w-full">
                <h3 className="font-heading text-lg text-foreground mb-2">
                  Stay Informed
                </h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Get practical privacy intelligence delivered to your inbox weekly.
                </p>
                <form onSubmit={handleSubscribe} className="space-y-3">
                  <Input
                    data-testid="substack-email-input"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11 rounded-xl bg-background border-border"
                  />
                  <Button
                    data-testid="substack-submit-button"
                    type="submit"
                    disabled={submitting}
                    className="w-full h-11 rounded-xl bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 btn-primary-hover"
                  >
                    {submitting ? "Subscribing..." : "Subscribe"}
                  </Button>
                </form>
                <div className="mt-4 pt-4 border-t border-border">
                  <a
                    href={data.substack_url || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="substack-open-link"
                    className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                  >
                    Open on Substack
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
